import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ExportFormatCode } from '@/core/models/practice.models';
import { Button } from '@/kit/Button';
import { FormField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Select, SelectOption } from '@/kit/Select';
import { useDebouncedSearch } from '../useDebouncedSearch';

export interface UserFilter {
  searchText?: string;
  statusType?: number;
  /** ap5-a3: 0 lists the live users, 1 lists the deleted ones. */
  recordStatus?: number;
}

interface UsersHeaderProps {
  onFilterChanged: (filter: UserFilter) => void;
  onExportRequested: (format: ExportFormatCode) => void;
}

/** Angular `UsersHeaderComponent`: the Users toolbar (Add button, search, status filter, export menu). */
export function UsersHeader({ onFilterChanged, onExportRequested }: UsersHeaderProps) {
  const navigate = useNavigate();
  /** -1 is the "All" option; the list opens on the Active users. */
  const [statusFilter, setStatusFilter] = useState(1);

  const onSearch = useDebouncedSearch((searchText) => onFilterChanged({ searchText }));

  // CommonService.onAddRouting(Modules.Users)
  const onAddClick = () => void navigate('/home/manage/addUser');

  /**
   * ap5-a3: -1 all, 1 active, 0 inactive, 2 deleted. Deleted users are kept, so they get their
   * own view rather than being mixed into the active/inactive status.
   */
  const onStatusChange = (value: number | null) => {
    setStatusFilter(value ?? -1);
    if (value === 2) {
      onFilterChanged({ statusType: -1, recordStatus: 1 });
      return;
    }
    onFilterChanged({ statusType: value ?? -1, recordStatus: 0 });
  };

  return (
    <div>
      <div className="content-header mb-0">
        <div className="content-header-left mb-1">
          <Button className="action-button primary-button" onClick={onAddClick}>
            <Icon name="add" />
            <span>Add User</span>
          </Button>
          <FormField className="search-field">
            <Icon name="search" />
            <input
              className="mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control"
              placeholder="Search"
              autoComplete="off"
              maxLength={100}
              onInput={(event) => onSearch(event.currentTarget.value)}
            />
          </FormField>
        </div>

        <div className="content-header-right mb-1">
          <Select
            className="w-150"
            openOnFocus
            panelClass="myPanelClass"
            value={statusFilter}
            onChange={(value) => onStatusChange(value as number | null)}
          >
            <SelectOption value={-1}>All</SelectOption>
            <SelectOption value={1}>Active</SelectOption>
            <SelectOption value={0}>Inactive</SelectOption>
            <SelectOption value={2}>Deleted</SelectOption>
          </Select>
          <div className="toolbar">
            <Button title="Export to Excel" onClick={() => onExportRequested(ExportFormatCode.Excel)}>
              <Icon name="border_all" className="icon-color-xls" />
            </Button>
            <Button title="Export to CSV" onClick={() => onExportRequested(ExportFormatCode.Csv)}>
              <Icon name="description" outlined className="icon-color-xls" />
            </Button>
            <Button title="Export to PDF" onClick={() => onExportRequested(ExportFormatCode.Pdf)}>
              <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
